import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddBook = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfUploaded, setPdfUploaded] = useState(false);

  // Prevent navigation during upload
  React.useEffect(() => {
    const preventNav = (e) => {
      if (uploadingPdf) {
        e.preventDefault();
        return false;
      }
    };
    
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', preventNav);
    
    return () => {
      window.removeEventListener('popstate', preventNav);
    };
  }, [uploadingPdf]);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: '',
    cover_image: '',
    price: '',
    age_range: '',
    pages: 0,
    category: '',
    description: '',
    featured: false,
    themes: '',
    pdf_url: ''
  });
  const [extractedData, setExtractedData] = useState(null);

  const handlePdfUpload = async (event) => {
    // Prevent any default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!file.name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploadingPdf(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const authHeaders = getAuthHeader();
      
      const response = await fetch(`${API_URL}/admin/books/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': authHeaders.Authorization
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const newFormData = {
        title: data.title || '',
        subtitle: '',
        author: data.author || '',
        cover_image: data.cover_image || '',
        price: '',
        age_range: '',
        pages: data.pages || 0,
        category: '',
        description: '',
        featured: false,
        themes: '',
        pdf_url: data.pdf_url || ''
      };

      setExtractedData(data);
      setFormData(newFormData);
      setPdfUploaded(true);
      
      toast.success('PDF uploaded! Book details extracted. Please complete the form below.', {
        duration: 10000
      });
      
      setTimeout(() => {
        window.scrollTo({ top: 400, behavior: 'smooth' });
      }, 500);
    } catch (error) {
      const errorMsg = error.message || 'Failed to process PDF';
      toast.error(errorMsg);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.author || !formData.category) {
      toast.error('Please fill in title, author, and category');
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    const bookData = {
      ...formData,
      price: parseFloat(formData.price),
      pages: parseInt(formData.pages) || 0,
      themes: formData.themes.split(',').map(t => t.trim()).filter(t => t)
    };

    try {
      await axios.post(`${API_URL}/admin/books`, bookData, {
        headers: getAuthHeader()
      });
      toast.success('✅ Book created successfully!');
      navigate('/admin/books');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save book');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/books')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Books
        </Button>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-8">Add New Book</h1>

      {/* PDF Upload Section - NOT in a form */}
      <div onClick={(e) => e.stopPropagation()}>
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="mr-2 h-5 w-5" />
              Step 1: Upload PDF Book
            </CardTitle>
          </CardHeader>
          <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Upload your PDF book file. We'll automatically extract the title, author, page count, and create a cover image.
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            style={{ display: 'none' }}
          />
          
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
              return false;
            }}
            disabled={uploadingPdf || pdfUploaded}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6"
          >
            {uploadingPdf ? '⏳ Processing PDF...' : pdfUploaded ? '✅ PDF Uploaded' : '📄 Choose PDF File'}
          </Button>
          
          {pdfUploaded && formData.title && (
            <p className="text-sm text-green-600 mt-2">Selected: {formData.title} by {formData.author}</p>
          )}

          {uploadingPdf && (
            <div className="mt-4 bg-blue-50 border-2 border-blue-400 rounded-lg p-6">
              <div className="flex items-center text-blue-700 mb-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span className="font-bold text-lg">Processing PDF...</span>
              </div>
              <div className="space-y-2 text-sm text-blue-600">
                <p>⏳ Uploading file...</p>
                <p>📖 Extracting book information...</p>
                <p>🖼️ Generating cover image...</p>
                <p className="font-semibold mt-3">Please wait, this may take a few seconds...</p>
              </div>
            </div>
          )}

          {pdfUploaded && (
            <div className="mt-4 bg-green-50 border-2 border-green-500 rounded-lg p-6 shadow-lg animate-pulse">
              <div className="flex items-center text-green-800 mb-3">
                <Check className="h-6 w-6 mr-2" />
                <span className="font-bold text-lg">✅ PDF Uploaded Successfully!</span>
              </div>
              <p className="text-sm text-green-700 mb-2">
                <strong>Extracted Information:</strong>
              </p>
              <ul className="text-sm text-green-700 space-y-1 ml-4">
                <li>• Title: <strong>{formData.title || 'NOT SET'}</strong></li>
                <li>• Author: <strong>{formData.author || 'NOT SET'}</strong></li>
                <li>• Pages: <strong>{formData.pages || 'NOT SET'}</strong></li>
                <li>• Cover: <strong>{formData.cover_image ? 'Generated ✓' : 'Using default'}</strong></li>
              </ul>
              {extractedData && (
                <div className="mt-3 p-2 bg-white rounded text-xs">
                  <strong>Debug - Raw Response:</strong>
                  <pre className="mt-1">{JSON.stringify(extractedData, null, 2)}</pre>
                </div>
              )}
              <div className="mt-4 bg-yellow-100 border border-yellow-400 rounded p-3">
                <p className="text-sm font-semibold text-yellow-800">
                  👇 Now scroll down and complete Step 2 below to finish!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Book Details Form */}
      <Card>
        <CardHeader>
          <CardTitle>Step 2: Complete Book Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Enter book title"
                />
              </div>
              <div>
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Optional subtitle"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="author">Author *</Label>
                <Input
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  required
                  placeholder="Author name"
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  placeholder="e.g., Science, Environment"
                />
              </div>
            </div>

            {/* Pricing & Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="price">Price ($) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="14.99"
                />
              </div>
              <div>
                <Label htmlFor="pages">Pages</Label>
                <Input
                  id="pages"
                  type="number"
                  value={formData.pages}
                  onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
                  placeholder="Auto-filled from PDF"
                />
              </div>
              <div>
                <Label htmlFor="age_range">Age Range *</Label>
                <Input
                  id="age_range"
                  value={formData.age_range}
                  onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                  required
                  placeholder="6-10 years"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={4}
                placeholder="Brief description of the book..."
              />
            </div>

            {/* Cover Image */}
            <div>
              <Label htmlFor="cover_image">Cover Image URL</Label>
              <Input
                id="cover_image"
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                placeholder="Auto-filled from PDF"
              />
              {formData.cover_image && (
                <img src={formData.cover_image} alt="Cover preview" className="mt-2 h-32 rounded border" />
              )}
            </div>

            {/* Themes */}
            <div>
              <Label htmlFor="themes">Themes (comma-separated)</Label>
              <Input
                id="themes"
                value={formData.themes}
                onChange={(e) => setFormData({ ...formData, themes: e.target.value })}
                placeholder="e.g., Adventure, Science, Nature"
              />
            </div>

            {/* Featured */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="featured" className="cursor-pointer">
                Feature this book on the homepage
              </Label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                Create Book
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/admin/books')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddBook;
